import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
  useDroppable, useDraggable
} from '@dnd-kit/core';
import {
  Phone, Mail, AlertCircle, CheckCircle2, Calendar, Clock, X,
  Users, Loader2, Search, RefreshCcw, FileText, Upload, Trash2,
  ChevronRight, AlertTriangle, Sparkles, Eye, ClipboardCheck, Target, Download
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import api, { formatErrorMessage } from '../utils/api';
import ReunionSemaforo from './ReunionSemaforo';

const ETAPA_STYLE = {
  'Por Contactar':      { bg: 'bg-amber-50',    border: 'border-amber-300',    text: 'text-amber-900',    head: 'bg-amber-500' },
  'Contactado':         { bg: 'bg-sky-50',      border: 'border-sky-300',      text: 'text-sky-900',      head: 'bg-sky-500' },
  'Sin Respuesta':      { bg: 'bg-orange-50',   border: 'border-orange-300',   text: 'text-orange-900',   head: 'bg-orange-500' },
  'En Precalificación': { bg: 'bg-violet-50',   border: 'border-violet-300',   text: 'text-violet-900',   head: 'bg-violet-600' },
  'Reunión Socios':     { bg: 'bg-indigo-50',   border: 'border-indigo-300',   text: 'text-indigo-900',   head: 'bg-indigo-600' },
  'Aceptado':           { bg: 'bg-emerald-50',  border: 'border-emerald-300',  text: 'text-emerald-900',  head: 'bg-emerald-600' },
  'Recuperado':         { bg: 'bg-teal-50',     border: 'border-teal-300',     text: 'text-teal-900',     head: 'bg-teal-600' },
  'Descartado':         { bg: 'bg-zinc-100',    border: 'border-zinc-300',     text: 'text-zinc-600',     head: 'bg-zinc-500' },
  'Perdido':            { bg: 'bg-slate-100',   border: 'border-slate-300',    text: 'text-slate-700',    head: 'bg-slate-500' }
};

const ETAPAS_PERMITEN_PRECAL = new Set(['Contactado', 'Sin Respuesta']);

// Helpers presentación tarjeta (nombre corto visible + fecha llegada compacta)
const getNombreCorto = (lead) => {
  const base = (lead?.nombre || lead?.email || '').trim();
  if (!base) return '—';
  // Si es un email, retornarlo tal cual (ya viene compacto)
  if (base.includes('@')) return base;
  const partes = base.split(/\s+/).filter(Boolean);
  if (partes.length <= 2) return base;
  // Primer nombre + primer apellido (2 tokens)
  return `${partes[0]} ${partes[1]}`;
};

const formatFechaCorta = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch (_e) {
    return null;
  }
};

const KanbanLeadsPipeline = ({ onLeadClick }) => {
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [modal, setModal] = useState(null); // { lead, destino }
  const [precalModal, setPrecalModal] = useState(null); // { lead }
  const [aplazoModal, setAplazoModal] = useState(null); // { lead }
  const [vista, setVista] = useState('kanban'); // 'kanban' | 'lista'
  const [verTodosModal, setVerTodosModal] = useState(null); // { etapa, leads }
  // Módulos separados por rol comercial:
  //  - 'sdr'     → Precalificación & Agendamiento (Gustavo Flores)
  //  - 'closing' → Closing & Pipeline de Cierre (Socios / Gustavo Carrasco)
  const [activeModule, setActiveModule] = useState('sdr');

  // Rol del usuario logueado (para permisos y visibilidad de columnas/acciones).
  // - SDR: role='Usuario' + areas incluye 'Comercial' (ej: Gustavo Flores).
  // - Closing/Admin: role='Administrador'/'Admin'/'Líder' (ej: Gustavo Carrasco, Socios).
  const currentUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch (_e) { return null; }
  }, []);
  const isAdminOrLider = currentUser && ['Administrador', 'Admin', 'Líder'].includes(currentUser.role);
  // SDR = Usuario del área Comercial SIN autorización granular para Closing.
  // El flag `permite_pipeline_closing` permite habilitar Módulo 2 a un SDR
  // específico sin promoverlo a Líder/Admin.
  const isSDR = currentUser && !isAdminOrLider
    && (currentUser.areas || []).includes('Comercial')
    && !currentUser.permite_pipeline_closing;
  const isClosingOrAdmin = !!isAdminOrLider || !!(currentUser && currentUser.permite_pipeline_closing);

  // Etapas por módulo. La visibilidad SDR es restringida (solo 3 columnas activas).
  const ETAPAS_SDR_FULL = ['Por Contactar', 'Contactado', 'Sin Respuesta', 'En Precalificación', 'Descartado', 'Recuperado'];
  const ETAPAS_SDR_RESTRICTED = ['Por Contactar', 'Contactado', 'En Precalificación'];
  const ETAPAS_SDR = isSDR ? ETAPAS_SDR_RESTRICTED : ETAPAS_SDR_FULL;
  const ETAPAS_CLOSING = ['Reunión Socios', 'Aceptado', 'Perdido'];

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = busqueda ? `?busqueda=${encodeURIComponent(busqueda)}` : '';
      const r = await api.get(`/leads/pipeline${params}`);
      setPipeline(r.data);
    } catch (e) {
      toast.error('Error cargando pipeline');
    } finally {
      setLoading(false);
    }
  }, [busqueda]);

  useEffect(() => { cargar(); }, [cargar]);

  const exportarCsv = async (etapa) => {
    try {
      const url = `/leads/export/csv?etapa=${encodeURIComponent(etapa)}`;
      const res = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.href = window.URL.createObjectURL(blob);
      link.download = `leads_${etapa.replace(/\s/g, '_')}_${ts}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Descargado: ${etapa}`);
    } catch (e) {
      toast.error(`Error al exportar: ${formatErrorMessage(e)}`);
    }
  };

  const transicionar = async (leadId, destino, payload = {}) => {
    try {
      await api.put(`/leads/${leadId}/transicion`, { etapa_destino: destino, ...payload });
      toast.success(`Lead movido a "${destino}"`);
      cargar();
      setModal(null);
    } catch (e) {
      toast.error(formatErrorMessage(e));
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    const leadId = active.id;
    const destino = over.id;
    if (!pipeline) return;

    // Encontrar el lead
    let lead = null;
    for (const etapa of pipeline.etapas) {
      const found = (pipeline.por_etapa[etapa] || []).find(l => l.id === leadId);
      if (found) { lead = found; break; }
    }
    if (!lead) return;
    if (lead.etapa === destino) return;

    // Verificar transición permitida
    const permitidos = pipeline.transiciones[lead.etapa] || [];
    if (!permitidos.includes(destino)) {
      toast.error(`No puedes mover de "${lead.etapa}" a "${destino}". Permitidos: ${permitidos.join(', ')}`);
      return;
    }

    // Si destino requiere modal de validación, abrir modal
    if (['Calificado', 'No Calificado', 'Descartado', 'Reunión Socios'].includes(destino)) {
      setModal({ lead, destino });
      return;
    }
    // transiciones simples sin form
    transicionar(leadId, destino);
  };

  if (loading && !pipeline) {
    return <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" /></div>;
  }
  if (!pipeline) return null;

  return (
    <div className="space-y-4" data-testid="kanban-pipeline">
      {/* Selector de Módulos de Pipeline: SDR (Precalificación) vs Closing (Cierre) */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-3 mb-2">
        <button
          type="button"
          onClick={() => setActiveModule('sdr')}
          data-testid="pipeline-module-sdr-btn"
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
            activeModule === 'sdr'
              ? 'bg-indigo-50 text-indigo-700 border border-indigo-200/80 shadow-sm'
              : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50 border border-transparent'
          }`}
        >
          <span className="w-2 h-2 rounded-full bg-indigo-500" />
          Módulo 1: Precalificación (SDR)
        </button>
        <button
          type="button"
          onClick={() => setActiveModule('closing')}
          data-testid="pipeline-module-closing-btn"
          disabled={isSDR}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
            isSDR
              ? 'opacity-40 cursor-not-allowed text-slate-400 border border-transparent'
              : activeModule === 'closing'
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200/80 shadow-sm'
                : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50 border border-transparent'
          }`}
          title={isSDR ? 'Sólo Closing/Admin puede operar en este módulo' : undefined}
        >
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          Módulo 2: Closing &amp; Cierre
        </button>
        <div className="ml-auto text-xs text-slate-500 hidden md:block">
          {activeModule === 'sdr'
            ? (isSDR
                ? 'SDR: precalifica leads. La decisión de agendar/descartar la toma Closing/Admin.'
                : 'Etapas iniciales del embudo: contacto, calificación y descarte.')
            : 'Reuniones agendadas, negociación y cierre comercial.'}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Buscar..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="pl-9 bg-white"
            data-testid="kanban-search"
          />
        </div>
        <Button variant="outline" size="sm" onClick={cargar}>
          <RefreshCcw className="w-3.5 h-3.5 mr-2" /> Recargar
        </Button>
        {/* Toggle Vista: Kanban / Lista */}
        <div className="inline-flex items-center rounded-md border border-slate-300 bg-white p-0.5 text-xs" role="tablist" aria-label="Vista del pipeline">
          <button
            type="button"
            onClick={() => setVista('kanban')}
            className={`px-2.5 py-1 rounded ${vista === 'kanban' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
            data-testid="vista-kanban-toggle"
            aria-pressed={vista === 'kanban'}
          >
            Kanban
          </button>
          <button
            type="button"
            onClick={() => setVista('lista')}
            className={`px-2.5 py-1 rounded ${vista === 'lista' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
            data-testid="vista-lista-toggle"
            aria-pressed={vista === 'lista'}
          >
            Lista
          </button>
        </div>
        <Badge variant="outline">{pipeline.total} leads</Badge>
      </div>

      {vista === 'kanban' ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          {/* Pipeline filtrado por módulo activo (y rol) */}
          <div className={`grid gap-2 ${
            activeModule === 'sdr'
              ? (ETAPAS_SDR.length <= 3
                  ? 'grid-cols-1 md:grid-cols-3'
                  : 'grid-cols-2 md:grid-cols-3 xl:grid-cols-6')
              : 'grid-cols-1 md:grid-cols-3'
          }`}>
            {(activeModule === 'sdr' ? ETAPAS_SDR : ETAPAS_CLOSING).map((etapa) => (
              <ColumnDroppable
                key={etapa}
                etapa={etapa}
                leads={pipeline.por_etapa[etapa] || []}
                onLeadClick={onLeadClick}
                onPrecalClick={(lead) => setPrecalModal({ lead })}
                onAplazoClick={(lead) => setAplazoModal({ lead })}
                onExportCsv={exportarCsv}
                onUpdated={cargar}
                onVerTodos={(et, ls) => setVerTodosModal({ etapa: et, leads: ls })}
                isClosingOrAdmin={isClosingOrAdmin}
                onDecidirPrecal={(lead, destino) => setModal({ lead, destino })}
              />
            ))}
          </div>
        </DndContext>
      ) : (
        <ListaLeadsView
          porEtapa={Object.fromEntries(
            Object.entries(pipeline.por_etapa || {}).filter(([et]) =>
              (activeModule === 'sdr' ? ETAPAS_SDR : ETAPAS_CLOSING).includes(et)
            )
          )}
          onLeadClick={onLeadClick}
          onExportCsv={exportarCsv}
        />
      )}

      {modal && (
        <ValidationModal
          modal={modal}
          motivosDescarte={pipeline.motivos_descarte}
          onClose={() => setModal(null)}
          onSubmit={(payload) => transicionar(modal.lead.id, modal.destino, payload)}
          onAgendarReunion={() => { cargar(); }}
        />
      )}

      {precalModal && (
        <PreCalModal
          lead={precalModal.lead}
          checklist={pipeline.precal_checklist}
          umbralPct={pipeline.precal_umbral_pct}
          onClose={() => setPrecalModal(null)}
          onSuccess={() => { setPrecalModal(null); cargar(); }}
        />
      )}

      {aplazoModal && (
        <AplazoModal
          lead={aplazoModal.lead}
          onClose={() => setAplazoModal(null)}
          onSuccess={() => { setAplazoModal(null); cargar(); }}
        />
      )}

      {verTodosModal && (
        <VerTodosModal
          etapa={verTodosModal.etapa}
          leads={verTodosModal.leads}
          onClose={() => setVerTodosModal(null)}
          onLeadClick={(id) => { setVerTodosModal(null); if (onLeadClick) onLeadClick(id); }}
        />
      )}
    </div>
  );
};

// ============================================================================
// VerTodosModal: muestra la lista completa (scrolleable) de leads de una etapa.
// ============================================================================
const VerTodosModal = ({ etapa, leads, onClose, onLeadClick }) => {
  const [filtro, setFiltro] = useState('');
  const filtrados = leads.filter(l => {
    if (!filtro) return true;
    const q = filtro.toLowerCase();
    return (
      (l.nombre || '').toLowerCase().includes(q) ||
      (l.email || '').toLowerCase().includes(q) ||
      (l.telefono || '').toLowerCase().includes(q) ||
      (l.empresa || '').toLowerCase().includes(q)
    );
  });
  const style = ETAPA_STYLE[etapa] || ETAPA_STYLE['Por Contactar'];
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-0 overflow-hidden" data-testid="ver-todos-modal">
        <DialogHeader className={`${style.head} text-white px-4 py-3`}>
          <DialogTitle className="text-white flex items-center gap-2">
            {etapa}
            <Badge className="bg-white/20 text-white text-xs">{leads.length}</Badge>
          </DialogTitle>
          <DialogDescription className="text-white/80 text-xs">
            Listado completo de leads en esta etapa. Haz clic en cualquiera para ver el detalle.
          </DialogDescription>
        </DialogHeader>
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Filtrar en esta etapa..."
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              className="pl-8 h-8 text-sm bg-white"
              data-testid="ver-todos-filtro"
              autoFocus
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1.5">
          {filtrados.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-8">Sin resultados</p>
          )}
          {filtrados.map(l => (
            <button
              key={l.id}
              type="button"
              onClick={() => onLeadClick && onLeadClick(l.id)}
              className="w-full text-left flex items-center gap-2 px-3 py-2 rounded border border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition"
              data-testid={`ver-todos-item-${l.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-slate-900 truncate">{l.nombre || l.email}</p>
                  {l.urgencia_critica && (
                    <Badge className="bg-rose-100 text-rose-800 border border-rose-300 text-[9px] h-4 px-1 uppercase font-bold">Urgente</Badge>
                  )}
                </div>
                <div className="flex items-center gap-3 text-[11px] text-slate-600 flex-wrap">
                  {l.telefono && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{l.telefono}</span>}
                  {l.email && <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{l.email}</span>}
                  {l.created_at && <span className="flex items-center gap-1 text-slate-400"><Calendar className="w-3 h-3" />{formatFechaCorta(l.created_at)}</span>}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
            </button>
          ))}
        </div>
        <DialogFooter className="px-4 py-2 border-t border-slate-200 bg-slate-50">
          <div className="flex items-center justify-between w-full text-xs text-slate-500">
            <span>{filtrados.length} de {leads.length} mostrados</span>
            <Button variant="outline" size="sm" onClick={onClose}>Cerrar</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// ListaLeadsView: vista alternativa tipo tabla de todos los leads.
// ============================================================================
const ListaLeadsView = ({ porEtapa, onLeadClick, onExportCsv }) => {
  const [etapaFiltro, setEtapaFiltro] = useState('Todas');
  const todos = Object.entries(porEtapa || {}).flatMap(([et, ls]) =>
    (ls || []).map(l => ({ ...l, _etapa: et }))
  );
  const filtrados = etapaFiltro === 'Todas' ? todos : todos.filter(l => l._etapa === etapaFiltro);
  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold text-slate-700">Etapa:</span>
        <Select value={etapaFiltro} onValueChange={setEtapaFiltro}>
          <SelectTrigger className="w-56 h-8 text-xs bg-white" data-testid="lista-etapa-filtro">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Todas">Todas las etapas</SelectItem>
            {Object.keys(porEtapa || {}).map(et => (
              <SelectItem key={et} value={et}>{et} ({(porEtapa[et] || []).length})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="ml-auto text-xs text-slate-500">{filtrados.length} lead(s)</span>
      </div>
      <div className="overflow-x-auto max-h-[680px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 sticky top-0 z-10">
            <tr className="border-b border-slate-200">
              <th className="text-left px-3 py-2 text-[11px] font-semibold text-slate-600 uppercase">Nombre</th>
              <th className="text-left px-3 py-2 text-[11px] font-semibold text-slate-600 uppercase">Teléfono</th>
              <th className="text-left px-3 py-2 text-[11px] font-semibold text-slate-600 uppercase">Email</th>
              <th className="text-left px-3 py-2 text-[11px] font-semibold text-slate-600 uppercase">Etapa</th>
              <th className="text-left px-3 py-2 text-[11px] font-semibold text-slate-600 uppercase">Ingresó</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {filtrados.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-slate-400 text-sm">Sin leads en esta selección</td></tr>
            )}
            {filtrados.map(l => {
              const st = ETAPA_STYLE[l._etapa] || ETAPA_STYLE['Por Contactar'];
              return (
                <tr
                  key={l.id}
                  onClick={() => onLeadClick && onLeadClick(l.id)}
                  className="border-b border-slate-100 hover:bg-blue-50 cursor-pointer transition"
                  data-testid={`lista-row-${l.id}`}
                >
                  <td className="px-3 py-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1">
                        <span className="font-medium text-slate-900 truncate">{l.nombre || l.email}</span>
                        {l.urgencia_critica && (
                          <Badge className="bg-rose-100 text-rose-800 border border-rose-300 text-[9px] h-4 px-1 uppercase font-bold">Urgente</Badge>
                        )}
                      </div>
                      {l.empresa && <span className="text-[11px] text-slate-500 truncate">{l.empresa}</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-700 text-xs whitespace-nowrap">{l.telefono || '—'}</td>
                  <td className="px-3 py-2 text-slate-700 text-xs truncate max-w-[220px]" title={l.email}>{l.email || '—'}</td>
                  <td className="px-3 py-2">
                    <Badge className={`${st.head} text-white text-[10px] font-semibold px-2 py-0.5`}>{l._etapa}</Badge>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{formatFechaCorta(l.created_at) || '—'}</td>
                  <td className="px-3 py-2"><ChevronRight className="w-4 h-4 text-slate-400" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ColumnDroppable = ({ etapa, leads, compact, onLeadClick, onPrecalClick, onAplazoClick, onExportCsv, onUpdated, onVerTodos, isClosingOrAdmin, onDecidirPrecal }) => {
  const { setNodeRef, isOver } = useDroppable({ id: etapa });
  const style = ETAPA_STYLE[etapa] || ETAPA_STYLE['Por Contactar'];
  return (
    <div
      ref={setNodeRef}
      className={`w-full min-w-0 rounded-lg border-2 ${style.border} ${style.bg} ${isOver ? 'ring-2 ring-blue-400 ring-offset-2' : ''}`}
      data-testid={`column-${etapa}`}
    >
      <div className={`${style.head} text-white px-2 py-1.5 rounded-t-md flex items-center justify-between gap-1`}>
        <span className="text-xs font-semibold truncate" title={etapa}>{etapa}</span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {leads.length > 0 && onExportCsv && (
            <button
              type="button"
              onClick={() => onExportCsv(etapa)}
              className="text-white/80 hover:text-white p-0.5 rounded hover:bg-white/20"
              title={`Descargar ${leads.length} leads como CSV`}
              data-testid={`column-${etapa}-export`}
            >
              <Download className="w-3 h-3" />
            </button>
          )}
          <Badge className="bg-white/20 text-white text-[10px] h-4 px-1.5">{leads.length}</Badge>
        </div>
      </div>
      <div className="p-1.5 space-y-1.5 min-h-[300px] max-h-[680px] overflow-y-auto">
        {leads.length === 0 && (
          <p className="text-[11px] text-slate-400 text-center py-4">Vacío</p>
        )}
        {leads.map(l => <LeadCard key={l.id} lead={l} onClick={onLeadClick} onPrecalClick={onPrecalClick} onAplazoClick={onAplazoClick} onUpdated={onUpdated} isClosingOrAdmin={isClosingOrAdmin} onDecidirPrecal={onDecidirPrecal} />)}
      </div>
      {leads.length > 0 && onVerTodos && (
        <button
          type="button"
          onClick={() => onVerTodos(etapa, leads)}
          className={`w-full px-2 py-1.5 border-t-2 ${style.border} ${style.text} text-[11px] font-semibold hover:bg-white/60 flex items-center justify-center gap-1 rounded-b-md transition`}
          data-testid={`column-${etapa}-ver-todos`}
        >
          Ver todos ({leads.length}) <ChevronRight className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

const LeadCard = ({ lead, onClick, onPrecalClick, onAplazoClick, onUpdated, isClosingOrAdmin, onDecidirPrecal }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: lead.id });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: isDragging ? 50 : 'auto', opacity: isDragging ? 0.5 : 1 } : {};
  const lastContact = lead.fecha_ultimo_contacto ? new Date(lead.fecha_ultimo_contacto).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' }) : null;
  const handleEyeClick = (e) => {
    e.stopPropagation(); e.preventDefault();
    if (onClick) onClick(lead.id);
  };
  const handlePrecalClick = (e) => {
    e.stopPropagation(); e.preventDefault();
    if (onPrecalClick) onPrecalClick(lead);
  };
  const handleAplazoClick = (e) => {
    e.stopPropagation(); e.preventDefault();
    if (onAplazoClick) onAplazoClick(lead);
  };
  const puedePrecal = ETAPAS_PERMITEN_PRECAL.has(lead.etapa);
  const precalScore = lead.precal_score_pct;
  const enPrecal = lead.etapa === 'En Precalificación';
  const overdue = lead.precal_overdue === true;
  const hoursRem = lead.precal_hours_remaining;
  const enSinResp = lead.etapa === 'Sin Respuesta';
  const srOverdue = lead.sin_respuesta_overdue === true;
  const srDaysRem = lead.sin_respuesta_days_remaining;
  const enDescartado = lead.etapa === 'Descartado';
  const descDaysRem = lead.descartado_days_remaining;
  const scoreColor = precalScore != null
    ? (precalScore >= 50 ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : 'bg-rose-100 text-rose-800 border-rose-300')
    : 'bg-slate-100 text-slate-700 border-slate-300';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-md border-2 ${overdue ? 'border-red-500 shadow-red-200 shadow-md ring-2 ring-red-300 animate-pulse-slow' : 'border-slate-200'} p-1.5 hover:shadow-md transition relative group`}
      data-testid={`lead-card-${lead.id}`}
    >
      {/* Banner URGENTE para vencidos */}
      {overdue && enPrecal && (
        <div className="absolute -top-2 left-2 px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider shadow z-10">
          ⚠ URGENTE · Vencido
        </div>
      )}

      {/* Botones de acción (no interfieren con drag) */}
      <div className="absolute top-0.5 right-0.5 flex gap-0.5 z-10">
        {enPrecal && overdue && onAplazoClick && (
          <button
            type="button"
            onClick={handleAplazoClick}
            onPointerDown={(e) => e.stopPropagation()}
            className="p-0.5 rounded bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300 shadow-sm"
            title="Aplazar 48h (requiere motivo)"
            data-testid={`lead-aplazar-${lead.id}`}
          >
            <Clock className="w-3 h-3" />
          </button>
        )}
        {puedePrecal && (
          <button
            type="button"
            onClick={handlePrecalClick}
            onPointerDown={(e) => e.stopPropagation()}
            className="p-0.5 rounded bg-violet-50 hover:bg-violet-100 text-violet-700 border border-violet-200 shadow-sm"
            title="Pre Calificar"
            data-testid={`lead-precal-${lead.id}`}
          >
            <ClipboardCheck className="w-3 h-3" />
          </button>
        )}
        {onClick && (
          <button
            type="button"
            onClick={handleEyeClick}
            onPointerDown={(e) => e.stopPropagation()}
            className="p-0.5 rounded hover:bg-blue-50 text-slate-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition"
            title="Ver detalle"
            data-testid={`lead-ver-${lead.id}`}
          >
            <Eye className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Zona drag (resto del contenido) */}
      <div
        {...listeners}
        {...attributes}
        onDoubleClick={(e) => { e.stopPropagation(); if (onClick) onClick(lead.id); }}
        className={`cursor-grab active:cursor-grabbing ${overdue ? 'pt-2' : ''}`}
        title="Doble click para ver detalle · Arrastra para mover"
      >
        {/* Encabezado: Nombre (primer nombre + primer apellido) + Badge Urgente */}
        <div className="mb-1 pr-12">
          <p
            className="text-[12px] font-semibold text-slate-900 leading-tight break-words"
            title={lead.nombre || lead.email}
            data-testid={`lead-nombre-${lead.id}`}
          >
            {getNombreCorto(lead)}
          </p>
          {lead.urgencia_critica && (
            <Badge className="mt-0.5 bg-rose-100 text-rose-800 border border-rose-300 text-[9px] h-3.5 px-1 uppercase tracking-wide font-bold">
              Urgente
            </Badge>
          )}
          {lead.empresa && (
            <p className="text-[10px] text-slate-500 truncate" title={lead.empresa}>{lead.empresa}</p>
          )}
        </div>

        {/* Datos de contacto visibles: teléfono + email + fecha de llegada */}
        <div className="space-y-0.5 mb-1 border-l-2 border-slate-100 pl-1.5">
          {lead.telefono && (
            <a
              href={`tel:${lead.telefono}`}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              className="flex items-center gap-1 text-[10px] text-slate-700 hover:text-blue-600 truncate"
              data-testid={`lead-telefono-${lead.id}`}
              title={lead.telefono}
            >
              <Phone className="w-2.5 h-2.5 flex-shrink-0" />
              <span className="truncate">{lead.telefono}</span>
            </a>
          )}
          {lead.email && (
            <a
              href={`mailto:${lead.email}`}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              className="flex items-center gap-1 text-[10px] text-slate-700 hover:text-blue-600 truncate"
              data-testid={`lead-email-${lead.id}`}
              title={lead.email}
            >
              <Mail className="w-2.5 h-2.5 flex-shrink-0" />
              <span className="truncate">{lead.email}</span>
            </a>
          )}
          {lead.created_at && (
            <div className="flex items-center gap-1 text-[10px] text-slate-500" title="Fecha de ingreso del lead">
              <Calendar className="w-2.5 h-2.5 flex-shrink-0" />
              <span className="truncate">{formatFechaCorta(lead.created_at)}</span>
            </div>
          )}
        </div>

        {/* Badge de score Pre-Cal cuando aplica (especialmente visible en columna En Precalificación) */}
        {(enPrecal || precalScore != null) && precalScore != null && (
          <div className={`mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-bold ${scoreColor}`} title="Score Pre Calificación">
            <Target className="w-3 h-3" /> Pre-Cal: {precalScore.toFixed(0)}%
            {precalScore >= 50 ? ' ✓' : ' ✗'}
          </div>
        )}

        {/* SLA Pre Calificación: contador en vivo */}
        {enPrecal && hoursRem != null && (
          <div className={`mt-1 inline-flex items-center gap-1 text-xs font-semibold ${overdue ? 'text-red-700' : hoursRem < 12 ? 'text-amber-700' : 'text-slate-600'}`}>
            <Clock className="w-3 h-3" />
            {overdue
              ? `Vencido hace ${Math.abs(hoursRem).toFixed(1)}h`
              : hoursRem < 24
                ? `Restan ${hoursRem.toFixed(1)}h`
                : `Restan ${Math.floor(hoursRem / 24)}d ${Math.floor(hoursRem % 24)}h`}
            {(lead.precal_aplazos || []).length > 0 && (
              <Badge variant="outline" className="ml-1 h-4 text-[10px] px-1">+{lead.precal_aplazos.length} aplazos</Badge>
            )}
          </div>
        )}

        {/* SLA Sin Respuesta: contador 5 días → auto-descarte */}
        {enSinResp && srDaysRem != null && (
          <div className={`mt-1 inline-flex items-center gap-1 text-xs font-semibold ${srOverdue ? 'text-red-700' : srDaysRem < 1 ? 'text-amber-700' : 'text-orange-700'}`}>
            <Clock className="w-3 h-3" />
            {srOverdue
              ? `Auto-descarte pendiente`
              : srDaysRem < 1
                ? `Restan ${Math.floor(srDaysRem * 24)}h`
                : `Restan ${Math.floor(srDaysRem)}d`}
          </div>
        )}

        {/* Indicador de Descartado: días hasta pasar a Perdido */}
        {enDescartado && descDaysRem != null && (
          <div className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500">
            <Clock className="w-3 h-3" />
            {descDaysRem > 0 ? `${Math.floor(descDaysRem)}d para Perdido` : 'Pasará a Perdido'}
          </div>
        )}

        {/* Marca de descarte automático */}
        {enDescartado && lead.descarte_automatico && (
          <Badge className="mt-1 bg-red-100 text-red-800 border-red-200 text-[10px]">⚙ Auto-descarte SLA</Badge>
        )}

        {/* Marca de recuperado */}
        {lead.etapa === 'Recuperado' && lead.fecha_recuperacion && (
          <p className="text-xs text-teal-700 mt-1 flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Recuperado por {lead.recuperado_por || 'comercial'}
          </p>
        )}

        {lead.motivo_descarte && (
          <Badge className="mt-2 bg-rose-100 text-rose-800 text-xs">
            {lead.motivo_descarte}{lead.motivo_descarte === 'Otro' && lead.motivo_descarte_otro ? `: ${lead.motivo_descarte_otro.slice(0, 20)}...` : ''}
          </Badge>
        )}
        {lead.reunion_socios?.fecha_hora && (
          <p className="text-xs text-indigo-700 mt-1 flex items-center gap-1">
            <Calendar className="w-3 h-3" /> {new Date(lead.reunion_socios.fecha_hora).toLocaleString('es-CL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </p>
        )}
        {lastContact && (
          <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Últ. contacto {lastContact}
            {lead.intentos_contacto > 0 && ` · ${lead.intentos_contacto} intentos`}
          </p>
        )}
        {lead.campana_nombre && (
          <p className="text-xs text-slate-400 truncate mt-1" title={lead.campana_nombre}>{lead.campana_nombre}</p>
        )}
        {lead.etapa === 'Reunión Socios' && lead.reunion_socios && (
          <ReunionSemaforo lead={lead} onUpdated={onUpdated} />
        )}
      </div>

      {/* Acciones de decisión en Precalificación — solo Closing/Admin */}
      {enPrecal && isClosingOrAdmin && onDecidirPrecal && (
        <div
          className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDecidirPrecal(lead, 'Descartado'); }}
            className="flex-1 py-1.5 px-2 bg-rose-50 text-rose-700 hover:bg-rose-100 rounded-md text-[11px] font-semibold transition-colors border border-rose-200/60"
            data-testid={`lead-decidir-descartar-${lead.id}`}
          >
            ✖ Descartar
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDecidirPrecal(lead, 'Reunión Socios'); }}
            className="flex-1 py-1.5 px-2 bg-emerald-600 text-white hover:bg-emerald-700 rounded-md text-[11px] font-semibold shadow-sm transition-colors"
            data-testid={`lead-decidir-agendar-${lead.id}`}
          >
            ➔ Agendar Reunión
          </button>
        </div>
      )}
    </div>
  );
};

const ValidationModal = ({ modal, motivosDescarte, onClose, onSubmit, onAgendarReunion }) => {
  const { lead, destino } = modal;
  const [form, setForm] = useState({
    telefono: lead.telefono || '',
    email: lead.email || '',
    motivos_calificacion: '',
    credenciales_contexto: '',
    motivo_descarte: '',
    motivo_descarte_otro: '',
    reunion_fecha_hora: '',
    notas: ''
  });
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  // Reunión Socios — nuevo formulario
  const [asistentesCatalogo, setAsistentesCatalogo] = useState([]);
  const [asistentesSel, setAsistentesSel] = useState(new Set());
  const [reunionForm, setReunionForm] = useState({
    duracion_min: 60,
    cliente_email: lead.email || '',
    descripcion_extra: '',
  });
  const [disponibilidad, setDisponibilidad] = useState(null);
  const [cargandoDisp, setCargandoDisp] = useState(false);
  const [slotElegido, setSlotElegido] = useState(null);
  const [agendando, setAgendando] = useState(false);

  useEffect(() => {
    if (destino === 'Reunión Socios') {
      api.get('/reuniones/asistentes-internos')
        .then(r => setAsistentesCatalogo(r.data.asistentes || []))
        .catch(() => toast.error('No se pudieron cargar los asistentes'));
    }
  }, [destino]);

  // Re-consulta disponibilidad cada vez que cambian asistentes o duración
  useEffect(() => {
    if (destino !== 'Reunión Socios') return;
    if (asistentesSel.size === 0) { setDisponibilidad(null); return; }
    let cancelado = false;
    setCargandoDisp(true);
    api.post('/reuniones/disponibilidad', {
      asistentes: Array.from(asistentesSel),
      duracion_min: reunionForm.duracion_min,
      dias_horizonte: 14,
    })
      .then(r => { if (!cancelado) setDisponibilidad(r.data); })
      .catch(() => { if (!cancelado) toast.error('Error consultando disponibilidad'); })
      .finally(() => { if (!cancelado) setCargandoDisp(false); });
    return () => { cancelado = true; };
  }, [destino, asistentesSel, reunionForm.duracion_min]);

  const loadDocs = useCallback(async () => {
    try {
      const r = await api.get(`/leads/${lead.id}/documentos`);
      setDocs(r.data.documentos || []);
    } catch {}
  }, [lead.id]);

  useEffect(() => {
    if (destino === 'Calificado') loadDocs();
  }, [destino, loadDocs]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      await api.post(`/leads/${lead.id}/documentos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Documento subido');
      loadDocs();
    } catch (err) {
      toast.error(formatErrorMessage(err));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const delDoc = async (id) => {
    if (!window.confirm('¿Eliminar documento?')) return;
    try {
      await api.delete(`/leads/${lead.id}/documentos/${id}`);
      loadDocs();
    } catch (err) {
      toast.error(formatErrorMessage(err));
    }
  };

  const titulos = {
    'Calificado': '✅ Calificar Lead',
    'No Calificado': '❌ Marcar No Calificado',
    'Descartado': '🗑️ Descartar Lead',
    'Reunión Socios': '📅 Agendar Reunión con Socios'
  };

  const submit = async () => {
    if (destino === 'Reunión Socios') {
      // Validar
      if (asistentesSel.size === 0) { toast.error('Selecciona al menos un asistente interno'); return; }
      if (!slotElegido) { toast.error('Selecciona un horario disponible'); return; }
      if (!reunionForm.cliente_email) { toast.error('El email del cliente es obligatorio'); return; }
      setAgendando(true);
      try {
        await api.post(`/leads/${lead.id}/reunion-socios/agendar`, {
          fecha_hora: slotElegido.start,
          duracion_min: reunionForm.duracion_min,
          cliente_email: reunionForm.cliente_email,
          asistentes_internos: Array.from(asistentesSel),
          descripcion_extra: reunionForm.descripcion_extra,
        });
        toast.success('Reunión agendada y eventos creados en Google Calendar');
        if (onAgendarReunion) onAgendarReunion();
        onClose();
      } catch (e) {
        toast.error(formatErrorMessage(e));
      } finally {
        setAgendando(false);
      }
      return;
    }
    onSubmit(form);
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{titulos[destino]}</DialogTitle>
          <DialogDescription>
            {lead.nombre || lead.email} · Etapa actual: <span className="inline-flex items-center px-1.5 py-0.5 rounded border border-slate-300 bg-white text-slate-700 text-xs ml-1">{lead.etapa}</span> → <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-900 text-white text-xs ml-1">{destino}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {destino === 'Calificado' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Teléfono *</Label>
                  <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="+56..." data-testid="cal-telefono" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="cliente@..." data-testid="cal-email" />
                </div>
              </div>
              <div>
                <Label>Motivos de calificación *</Label>
                <Textarea rows={3} placeholder="¿Por qué es un cliente potencial?" value={form.motivos_calificacion} onChange={(e) => setForm({ ...form, motivos_calificacion: e.target.value })} data-testid="cal-motivos" />
              </div>
              <div>
                <Label>Credenciales / Contexto (opcional)</Label>
                <Textarea rows={2} placeholder="Info adicional del perfil..." value={form.credenciales_contexto} onChange={(e) => setForm({ ...form, credenciales_contexto: e.target.value })} />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Repositorio documental (opcional)
                </Label>
                <div className="mt-1 space-y-2">
                  <Input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.txt,.csv" onChange={upload} disabled={uploading} data-testid="cal-upload" />
                  {uploading && <p className="text-xs text-slate-500"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Subiendo...</p>}
                  {docs.length > 0 && (
                    <div className="space-y-1">
                      {docs.map(d => (
                        <div key={d.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded text-xs">
                          <FileText className="w-4 h-4 text-slate-500" />
                          <span className="flex-1 truncate">{d.filename}</span>
                          <span className="text-slate-400">{(d.size_bytes / 1024).toFixed(0)}KB</span>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-rose-600" onClick={() => delDoc(d.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {(destino === 'No Calificado' || destino === 'Descartado') && (
            <>
              <div>
                <Label>Motivo de descarte *</Label>
                <Select value={form.motivo_descarte} onValueChange={(v) => setForm({ ...form, motivo_descarte: v })}>
                  <SelectTrigger data-testid="motivo-descarte"><SelectValue placeholder="Selecciona un motivo..." /></SelectTrigger>
                  <SelectContent>
                    {motivosDescarte.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.motivo_descarte === 'Otro' && (
                <div>
                  <Label>Descripción del motivo *</Label>
                  <Textarea rows={2} value={form.motivo_descarte_otro} onChange={(e) => setForm({ ...form, motivo_descarte_otro: e.target.value })} data-testid="motivo-otro" />
                </div>
              )}
            </>
          )}

          {destino === 'Reunión Socios' && (
            <div className='space-y-5' data-testid='agendar-reunion-form'>
              {/* Paso 1: Asistentes */}
              <div>
                <Label className='mb-2 block flex items-center gap-2'>
                  <span className='inline-flex items-center justify-center w-5 h-5 rounded-full bg-indigo-600 text-white text-xs font-bold'>1</span>
                  Selecciona los asistentes internos
                </Label>
                <div className='border border-slate-200 rounded-lg divide-y divide-slate-100' data-testid='asistentes-list'>
                  {asistentesCatalogo.length === 0 && (
                    <p className='p-3 text-sm text-slate-500 text-center'>Cargando asistentes...</p>
                  )}
                  {asistentesCatalogo.map(a => (
                    <label
                      key={a.email}
                      className='flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer'
                      data-testid={`asistente-${a.email}`}
                    >
                      <input
                        type='checkbox'
                        checked={asistentesSel.has(a.email)}
                        onChange={() => {
                          const ns = new Set(asistentesSel);
                          if (ns.has(a.email)) ns.delete(a.email); else ns.add(a.email);
                          setAsistentesSel(ns);
                          setSlotElegido(null);  // resetear slot al cambiar asistentes
                        }}
                        className='w-4 h-4 accent-indigo-600'
                      />
                      <div className='flex-1'>
                        <p className='text-sm font-medium text-slate-900'>{a.name}</p>
                        <p className='text-xs text-slate-500'>{a.cargo} · {a.email}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Paso 2: Disponibilidad */}
              <div>
                <div className='flex items-center justify-between mb-2'>
                  <Label className='flex items-center gap-2 mb-0'>
                    <span className='inline-flex items-center justify-center w-5 h-5 rounded-full bg-indigo-600 text-white text-xs font-bold'>2</span>
                    Elige un horario disponible
                  </Label>
                  <div className='flex items-center gap-2'>
                    <Label className='text-xs text-slate-500 mb-0'>Duración:</Label>
                    <select
                      value={reunionForm.duracion_min}
                      onChange={(e) => { setReunionForm({ ...reunionForm, duracion_min: parseInt(e.target.value) }); setSlotElegido(null); }}
                      className='h-7 text-xs border border-slate-300 rounded px-2'
                      data-testid='reunion-duracion'
                    >
                      <option value={30}>30 min</option>
                      <option value={45}>45 min</option>
                      <option value={60}>60 min</option>
                      <option value={90}>90 min</option>
                    </select>
                  </div>
                </div>
                <p className='text-xs text-slate-500 mb-2'>
                  Solo se muestran slots libres en Google Calendar de todos los asistentes seleccionados (Lun-Vie 10:00–17:00 hrs).
                </p>

                {asistentesSel.size === 0 ? (
                  <div className='text-center py-8 border border-dashed border-slate-300 rounded-lg text-sm text-slate-500'>
                    Selecciona al menos un asistente para ver la disponibilidad.
                  </div>
                ) : cargandoDisp ? (
                  <div className='text-center py-8 border border-dashed border-slate-300 rounded-lg'>
                    <Loader2 className='w-5 h-5 animate-spin mx-auto text-indigo-500' />
                    <p className='text-xs text-slate-500 mt-2'>Consultando Google Calendar...</p>
                  </div>
                ) : disponibilidad?.dias?.length === 0 ? (
                  <div className='text-center py-8 border border-dashed border-rose-300 rounded-lg text-sm text-rose-600'>
                    No hay slots libres en los próximos 14 días hábiles con estos asistentes.
                  </div>
                ) : (
                  <div
                    className='max-h-72 overflow-y-auto pr-1 space-y-2 border border-slate-200 rounded-lg p-2 bg-slate-50/40'
                    data-testid='slots-grid'
                  >
                    {(disponibilidad?.dias || []).map(dia => (
                      <div key={dia.day_key}>
                        <p className='text-xs font-semibold text-slate-700 mb-1 sticky top-0 bg-slate-50/95 py-0.5 backdrop-blur-sm'>
                          {dia.day_label}
                          <span className='ml-2 text-slate-400 font-normal'>· {dia.slots.length} libres</span>
                        </p>
                        <div className='grid grid-cols-6 gap-1'>
                          {dia.slots.map(s => {
                            const selected = slotElegido?.start === s.start;
                            return (
                              <button
                                key={s.start}
                                onClick={() => setSlotElegido({ start: s.start, end: s.end, label: `${dia.day_label} · ${s.hour_label}` })}
                                className={`text-xs h-8 rounded border transition ${
                                  selected
                                    ? 'bg-indigo-600 text-white border-indigo-700 shadow-md'
                                    : 'bg-white border-slate-200 text-slate-700 hover:border-indigo-400 hover:bg-indigo-50'
                                }`}
                                data-testid={`slot-${s.start}`}
                              >
                                {s.hour_label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {slotElegido && (
                  <p className='mt-2 text-xs text-indigo-700 font-medium' data-testid='slot-elegido-label'>
                    Horario seleccionado: {slotElegido.label}
                  </p>
                )}
              </div>

              {/* Paso 3: Email cliente + notas */}
              <div>
                <Label className='mb-2 block flex items-center gap-2'>
                  <span className='inline-flex items-center justify-center w-5 h-5 rounded-full bg-indigo-600 text-white text-xs font-bold'>3</span>
                  Datos del cliente
                </Label>
                <div className='space-y-2'>
                  <Input
                    type='email'
                    value={reunionForm.cliente_email}
                    onChange={(e) => setReunionForm({ ...reunionForm, cliente_email: e.target.value })}
                    placeholder='cliente@empresa.cl'
                    data-testid='reunion-cliente-email'
                  />
                  <Textarea
                    rows={2}
                    value={reunionForm.descripcion_extra}
                    onChange={(e) => setReunionForm({ ...reunionForm, descripcion_extra: e.target.value })}
                    placeholder='Notas para la descripción del evento (opcional)'
                    data-testid='reunion-descripcion-extra'
                  />
                </div>
                <p className='text-xs text-slate-500 mt-1'>
                  El cliente recibirá invitación de Google Calendar y un recordatorio 09:00 AM con botones de Confirmar/Reagendar/Anular.
                </p>
              </div>
            </div>
          )}

          <div>
            <Label>Notas (opcional)</Label>
            <Textarea rows={2} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={agendando} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-confirmar-transicion">
            {agendando ? <><Loader2 className='w-4 h-4 mr-2 animate-spin' />Agendando…</> : 'Confirmar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ========== MODAL DE PRE CALIFICACIÓN (12 ítems ponderados) ========== */
export const PreCalModal = ({ lead, checklist: checklistProp, umbralPct: umbralPctProp, onClose, onSuccess }) => {
  const [checklist, setChecklist] = useState(checklistProp || null);
  const [umbralPct, setUmbralPct] = useState(umbralPctProp || 50);
  const [marcados, setMarcados] = useState(new Set(lead.precal_items_marcados || []));
  const [notas, setNotas] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!checklistProp) {
      api.get('/leads/precalificacion/checklist')
        .then(r => { setChecklist(r.data.items); setUmbralPct(r.data.umbral_pct); })
        .catch(() => toast.error('Error cargando checklist'));
    }
  }, [checklistProp]);

  const toggle = (key) => {
    setMarcados(prev => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  const score = (checklist || []).reduce((acc, item) => acc + (marcados.has(item.key) ? item.peso : 0), 0);
  const aprobado = score >= (umbralPct || 50);

  const guardar = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/leads/${lead.id}/precalificar`, {
        items_marcados: Array.from(marcados),
        notas
      });
      const s = r.data.score_pct;
      const aprob = r.data.aprobado;
      toast.success(aprob
        ? `Pre Calificación: ${s.toFixed(0)}% ✓ — Puede avanzar a "Calificado"`
        : `Pre Calificación: ${s.toFixed(0)}% — Lead movido a "En Precalificación"`,
        { duration: 5000 }
      );
      if (onSuccess) onSuccess(r.data);
    } catch (e) {
      toast.error(formatErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (!checklist) {
    return (
      <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
        <DialogContent><Loader2 className="w-6 h-6 animate-spin mx-auto" /></DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="precal-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-violet-600" /> Pre Calificación
          </DialogTitle>
          <DialogDescription>
            {lead.nombre || lead.email} · Etapa actual: <span className="inline-flex items-center px-1.5 py-0.5 rounded border border-slate-300 bg-white text-slate-700 text-xs ml-1">{lead.etapa}</span>
          </DialogDescription>
        </DialogHeader>

        {/* Score live */}
        <div className={`p-3 rounded-lg border-2 ${aprobado ? 'bg-emerald-50 border-emerald-300' : 'bg-slate-50 border-slate-300'}`}>
          <div className="flex items-center justify-between mb-1.5">
            <div>
              <p className="text-xs text-slate-600 uppercase font-semibold tracking-wide">Score Pre Calificación</p>
              <p className={`text-4xl font-bold leading-none ${aprobado ? 'text-emerald-700' : 'text-slate-700'}`}>
                {score.toFixed(0)}<span className="text-2xl">%</span>
              </p>
            </div>
            <div className="text-right">
              <Badge className={aprobado ? 'bg-emerald-600 text-white' : 'bg-slate-500 text-white'}>
                {aprobado ? `≥ ${umbralPct}% · Puede calificar` : `< ${umbralPct}% · Queda en Precalificación`}
              </Badge>
              <p className="text-xs text-slate-500 mt-1">Umbral mínimo: {umbralPct}%</p>
            </div>
          </div>
          <div className="w-full bg-white rounded-full h-2 mt-2 overflow-hidden border border-slate-200">
            <div
              className={`h-full transition-all ${aprobado ? 'bg-emerald-500' : 'bg-rose-400'}`}
              style={{ width: `${Math.min(100, score)}%` }}
            />
          </div>
        </div>

        {/* Checklist */}
        <div className="space-y-1.5 mt-3">
          <Label className="text-sm font-semibold">Checklist (12 ítems ponderados)</Label>
          {checklist.map(item => {
            const checked = marcados.has(item.key);
            return (
              <label
                key={item.key}
                className={`flex items-start gap-2 p-2.5 rounded border cursor-pointer transition ${
                  checked ? 'bg-violet-50 border-violet-300' : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`precal-item-${item.key}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(item.key)}
                  className="mt-0.5 w-4 h-4 accent-violet-600"
                />
                <span className="flex-1 text-sm text-slate-800 leading-tight">{item.label}</span>
                <Badge variant="outline" className={`text-xs ${item.peso >= 15 ? 'border-violet-400 text-violet-700' : item.peso >= 10 ? 'border-slate-400 text-slate-700' : 'border-slate-300 text-slate-500'}`}>
                  {item.peso}%
                </Badge>
              </label>
            );
          })}
        </div>

        {/* Notas */}
        <div className="mt-3">
          <Label className="text-sm">Notas (opcional)</Label>
          <Textarea rows={2} value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Observaciones del pre-calificador..." />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={guardar}
            disabled={saving}
            className={aprobado ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-violet-600 hover:bg-violet-700 text-white'}
            data-testid="precal-submit"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</> : <>Guardar Pre Calificación</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ========== MODAL DE APLAZAMIENTO SLA (Pre Calificación >48h) ========== */
const AplazoModal = ({ lead, onClose, onSuccess }) => {
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);

  const guardar = async () => {
    if (!motivo.trim() || motivo.trim().length < 3) {
      toast.error('El motivo es obligatorio (mín. 3 caracteres)');
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/leads/${lead.id}/aplazar-precalificacion`, { motivo: motivo.trim() });
      toast.success(`Aplazado +48h · Total aplazos: ${r.data.total_aplazos}`);
      if (onSuccess) onSuccess(r.data);
    } catch (e) {
      toast.error(formatErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="aplazo-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-600" /> Aplazar Pre Calificación (+48h)
          </DialogTitle>
          <DialogDescription>
            {lead.nombre || lead.email} · La Pre Calificación venció. Indica el motivo para extender el plazo 48 horas adicionales.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {(lead.precal_aplazos || []).length > 0 && (
            <Card className="p-2 bg-amber-50 border-amber-200">
              <p className="text-xs text-amber-900 font-semibold">{lead.precal_aplazos.length} aplazo(s) anterior(es):</p>
              {lead.precal_aplazos.slice(-3).map(a => (
                <p key={a.id} className="text-xs text-amber-800 mt-1">
                  • {new Date(a.at).toLocaleString('es-CL')} · {a.by} → "{a.motivo.slice(0, 80)}"
                </p>
              ))}
            </Card>
          )}

          <div>
            <Label className="text-sm">Motivo del aplazamiento <span className="text-red-600">*</span></Label>
            <Textarea
              rows={3}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej: Cliente solicitó info adicional · Esperando documentación SII · Vacaciones del cliente..."
              data-testid="aplazo-motivo"
              autoFocus
            />
          </div>

          <Card className="p-2 bg-slate-50 border-slate-200">
            <p className="text-xs text-slate-700">
              ⚠ Si no aplazas con motivo, el lead deberá ser <strong>descartado</strong> obligatoriamente.
            </p>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={saving || motivo.trim().length < 3} className="bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50" data-testid="aplazo-submit">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Aplazando...</> : <><Clock className="w-4 h-4 mr-2" /> Aplazar +48h</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default KanbanLeadsPipeline;